/**
 * What has drifted on an item since its Plan of Record was authorized
 * (item_plan_drift, migration 936). Read by the item page in Action.
 */

import { getSupabaseClient } from './supabaseClient';

export type ItemPlanDriftLine =
  | { kind: 'moved'; was: string | null; was_index: number | null; now: string | null; now_index?: number | null }
  | { kind: 'pattern'; list_key: 'work_type' | 'launch_type' | 'investment_type'; type_value: string; was_version: number | null; now_version: number; reason: string | null }
  | { kind: 'load'; team: string; booked_cu: number; expected_cu: number }
  | { kind: 'override'; part: string; field: string; value: unknown; reason: string | null; at: string }
  | { kind: 'changed'; field: 'effort_capacity_units' | 'work_type' | 'launch_type' | 'investment_type' | 'item_category' | 'altitude_level'; was: unknown; now: unknown }
  | { kind: 'kept_out'; held_version: number; latest_version: number; cycle_id: string | null; cycle_name: string | null };

export interface ItemPlanDrift {
  authorizedAt: string | null;
  /** False when the plan was authorized before the item's state was recorded (936). */
  recorded: boolean;
  /** May the signed-in person adopt or keep a pattern version for this plan? (938) */
  mayDecide: boolean;
  lines: ItemPlanDriftLine[];
}

export async function fetchItemPlanDrift(itemId: string, planCycleId: string): Promise<ItemPlanDrift> {
  // item_plan_drift arrives with migration 936 and is not in the generated types yet.
  const { data, error } = await getSupabaseClient().rpc('item_plan_drift', { p_item_id: itemId, p_plan_cycle_id: planCycleId });
  if (error) throw new Error(error.message);
  const d = (data ?? {}) as { authorized_at?: string | null; recorded?: boolean; may_decide?: boolean; lines?: ItemPlanDriftLine[] };
  return { authorizedAt: d.authorized_at ?? null, recorded: d.recorded === true, mayDecide: d.may_decide === true, lines: Array.isArray(d.lines) ? d.lines : [] };
}

/** A type whose pattern has moved on since the plan was authorized (plan_pattern_changes, 938). */
export interface PlanPatternChange {
  listKey: 'work_type' | 'launch_type' | 'investment_type';
  typeValue: string;
  items: number;
  wasVersion: number | null;
  nowVersion: number;
  reason: string | null;
}

export async function fetchPlanPatternChanges(planCycleId: string): Promise<{ mayDecide: boolean; changes: PlanPatternChange[] }> {
  const { data, error } = await getSupabaseClient().rpc('plan_pattern_changes', { p_plan_cycle_id: planCycleId });
  if (error) throw new Error(error.message);
  const d = (data ?? {}) as { may_decide?: boolean; changes?: Array<Record<string, unknown>> };
  return {
    mayDecide: d.may_decide === true,
    changes: (d.changes ?? []).map((c) => ({
      listKey: c.list_key as PlanPatternChange['listKey'],
      typeValue: String(c.type_value),
      items: Number(c.items),
      wasVersion: c.was_version == null ? null : Number(c.was_version),
      nowVersion: Number(c.now_version),
      reason: (c.reason as string | null) ?? null,
    })),
  };
}

/** Adopt the current pattern version for the plan's items, or keep the earlier one (decide_plan_pattern, 938). */
export async function decidePlanPattern(input: {
  planCycleId: string;
  listKey: string;
  typeValue: string;
  decision: 'adopt' | 'keep';
  itemId: string | null;
  reason: string;
}): Promise<{ error: string | null; items?: number }> {
  const { data, error } = await getSupabaseClient().rpc('decide_plan_pattern', {
    p_plan_cycle_id: input.planCycleId,
    p_list_key: input.listKey,
    p_type_value: input.typeValue,
    p_decision: input.decision,
    p_item_id: input.itemId,
    p_reason: input.reason,
  });
  if (error) return { error: error.message };
  return { error: null, items: (data as { items?: number } | null)?.items };
}

/** One drift line in words, as Needs attention shows it. */
export interface DriftWords {
  mark: string;
  tone: 'coral' | 'mustard' | 'slate';
  text: string;
}

const LIST_NAME: Record<string, string> = { work_type: 'work type', launch_type: 'launch type', investment_type: 'investment type' };

/** Drift lines in plain words. `typeLabel` names a type value in its list. */
export function describeDrift(lines: ItemPlanDriftLine[], typeLabel: (listKey: string, value: string) => string): DriftWords[] {
  const label = (key: string, value: unknown) => (typeof value === 'string' && value ? typeLabel(key, value) : 'none');
  return lines.map((l): DriftWords => {
    switch (l.kind) {
      case 'moved':
        return {
          mark: '! Moved',
          tone: 'mustard',
          text: l.now
            ? `Placed in ${l.now}${l.was ? `; it was in ${l.was} when the plan was authorized` : ', after the plan was authorized'}.`
            : `No longer placed; it was in ${l.was ?? 'a period'} when the plan was authorized.`,
        };
      case 'load': {
        const team = l.team.charAt(0).toUpperCase() + l.team.slice(1);
        return { mark: '! Over', tone: 'coral', text: `${team} has ${l.booked_cu} CU booked; the item’s patterns expect ${l.expected_cu}.` };
      }
      case 'pattern':
        return {
          mark: '! Pattern',
          tone: 'mustard',
          text: `The ${label(l.list_key, l.type_value)} pattern changed since the plan (v${l.was_version} → v${l.now_version})${l.reason ? `: “${l.reason}”` : ''}.`,
        };
      case 'override':
        return { mark: '! Override', tone: 'mustard', text: `${l.part}: ${l.field} was overridden for this item after the plan was authorized${l.reason ? ` — “${l.reason}”` : ''}.` };
      case 'changed':
        return {
          mark: '! Changed',
          tone: 'mustard',
          text:
            l.field === 'effort_capacity_units'
              ? `Effort is ${l.now ?? 'not set'} CU now; the plan holds ${l.was ?? 'not set'} CU.`
              : l.field === 'altitude_level'
                ? `Altitude is ${l.now ?? 'not set'} now; the plan holds ${l.was ?? 'not set'}.`
                : l.field === 'item_category'
                  ? `Category is ${l.now ?? 'not set'} now; the plan holds ${l.was ?? 'not set'}.`
                  : `The ${LIST_NAME[l.field]} is ${label(l.field, l.now)} now; the plan holds ${label(l.field, l.was)}.`,
        };
      case 'kept_out':
        return {
          mark: 'Kept out',
          tone: 'slate',
          text: `The plan holds version ${l.held_version}. Version ${l.latest_version}${l.cycle_name ? `, made in ${l.cycle_name},` : ''} stays out until the plan is amended.`,
        };
    }
  });
}
